import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getBrokerLiveAccountState,
  getBrokerLiveAccountStateStatus,
  getBrokerLiveConnection,
  getBrokerLiveExecutionsStatus,
  getBrokerLivePositions,
  getBrokerLiveRecentExecutions,
  getBrokerLiveSyncStatus,
  refreshBrokerLiveExecutions,
  refreshBrokerLivePositions,
} from '../api/brokerConnections';
import useAuthenticatedRequest from './useAuthenticatedRequest';

const pickFirst = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.toLowerCase());
  return false;
};

const normalizeStateName = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase().replace(/[-\s]+/g, '_');
  const aliases = {
    ok: 'connected',
    ready: 'connected',
    healthy: 'connected',
    live: 'connected',
    authorization_expired: 'auth_expired',
    auth_expired: 'auth_expired',
    stale: 'stale',
    degraded: 'degraded',
    disconnected: 'disconnected',
    unavailable: 'unavailable',
    error: 'degraded',
  };
  return aliases[normalized] || normalized;
};

const normalizeDataStatus = (value) => {
  if (!value) return null;
  const normalized = String(value).trim().toLowerCase().replace(/[-\s]+/g, '_');
  const aliases = {
    ok: 'available',
    ready: 'available',
    success: 'available',
    fresh: 'available',
    authorization_expired: 'auth_expired',
  };
  return aliases[normalized] || normalized;
};

const arrayFrom = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const accountListFrom = (value) =>
  arrayFrom(value)
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return entry;
      return pickFirst(entry.account_id, entry.account, entry.id, entry.name);
    })
    .filter((entry) => entry !== undefined && entry !== null && entry !== '')
    .map((entry) => String(entry));

const countPayload = (data, keys = []) => {
  const explicit = pickFirst(
    data?.count,
    data?.total,
    data?.position_count,
    data?.account_count,
    data?.execution_count,
    data?.meta?.count,
    data?.meta?.total,
    data?.meta?.position_count,
    data?.meta?.account_count,
    data?.meta?.execution_count
  );
  if (Number.isFinite(Number(explicit))) return Number(explicit);
  for (const key of keys) {
    const value = data?.[key];
    if (Array.isArray(value)) return value.length;
    if (value && typeof value === 'object') return Object.keys(value).length;
  }
  return null;
};

const unavailableSnapshot = (label) => ({
  data_status: 'unavailable',
  source_label: label,
  connection_state: 'unavailable',
  as_of: null,
  stale: true,
  available: false,
  count: null,
  message: null,
  meta: {},
  raw: null,
});

const normalizeConnection = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      connected: false,
      connection_state: 'unavailable',
      health_state: 'unavailable',
      stale: true,
      degraded: false,
      auth_expired: false,
      last_heartbeat_at: null,
      checked_at: null,
      account_ids: [],
      message: null,
      raw: null,
    };
  }

  const rawState = normalizeStateName(
    pickFirst(data.connection_state, data.connection_status, data.health_state, data.health, data.status, data.state)
  );
  const authExpired = toBoolean(data.auth_expired || data.reauth_required) || rawState === 'auth_expired';
  const degraded = toBoolean(data.degraded) || rawState === 'degraded';
  const stale = toBoolean(data.stale || data.is_stale) || rawState === 'stale';
  const connected = toBoolean(data.connected || data.authenticated) || rawState === 'connected';
  const connectionState = authExpired
    ? 'auth_expired'
    : degraded
    ? 'degraded'
    : connected
    ? 'connected'
    : rawState || 'unavailable';
  const healthState = authExpired
    ? 'auth_expired'
    : degraded
    ? 'degraded'
    : stale
    ? 'stale'
    : connected
    ? 'healthy'
    : connectionState;

  return {
    connected,
    connection_state: connectionState,
    health_state: healthState,
    stale,
    degraded,
    auth_expired: authExpired,
    source: data.source || null,
    authenticated: toBoolean(data.authenticated),
    competing: toBoolean(data.competing),
    reauth_required: toBoolean(data.reauth_required),
    last_heartbeat_at: pickFirst(data.last_heartbeat_at, data.last_heartbeat, data.heartbeat_at, data.last_seen_at),
    last_success_at: pickFirst(data.last_success_at, data.last_success),
    checked_at: pickFirst(data.checked_at, data.last_checked_at, data.last_checked, data.updated_at, data.as_of),
    account_ids: accountListFrom(pickFirst(data.account_ids, data.accounts, data.account_id)),
    message: pickFirst(data.message, data.detail, data.last_error, data.error),
    raw: data,
  };
};

const normalizeSnapshot = (data, { label, itemKeys = [] } = {}) => {
  if (!data || typeof data !== 'object') return unavailableSnapshot(label);
  const dataStatus =
    pickFirst(data.data_status, data.availability, data.status) ||
    (toBoolean(data.available) ? 'available' : 'unavailable');
  const normalizedStatus = normalizeDataStatus(dataStatus) || String(dataStatus).toLowerCase();
  const available =
    toBoolean(data.available) ||
    ['available', 'connected', 'fresh', 'live', 'ok', 'ready', 'success'].includes(normalizedStatus);
  return {
    data_status: normalizedStatus,
    source_label: data.source_label || label,
    connection_state: normalizeStateName(pickFirst(data.connection_state, data.connection_status)) || null,
    as_of: pickFirst(data.as_of, data.snapshot_at, data.last_refresh_at, data.last_synced_at, data.updated_at),
    stale: toBoolean(data.stale || data.is_stale),
    available,
    count: countPayload(data, itemKeys),
    message: pickFirst(data.message, data.detail, data.last_error, data.error),
    meta: data.meta && typeof data.meta === 'object' ? data.meta : {},
    raw: data,
  };
};

const DEFAULT_STATE = {
  connection: normalizeConnection(null),
  positions: unavailableSnapshot('Live position snapshots unavailable'),
  accountState: unavailableSnapshot('Account state unavailable'),
  accountStateStatus: unavailableSnapshot('Account state status unavailable'),
  recentExecutions: unavailableSnapshot('Near-live executions unavailable'),
  executionsStatus: unavailableSnapshot('Near-live execution status unavailable'),
  syncStatus: unavailableSnapshot('Broker-live sync status unavailable'),
};

export const extractBrokerLiveErrorMessage = (err, fallback = 'Could not load broker-live state.') => {
  if (!err) return fallback;
  const data = err.response?.data;
  const msg = data?.message || data?.detail || data?.error;
  if (msg) return String(msg);
  if (err.message) return err.message;
  return fallback;
};

export default function useIbkrBrokerLiveState({ enabled = true } = {}) {
  const { isLoaded, isSignedIn } = useAuthenticatedRequest();
  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const load = useCallback(async () => {
    if (!enabled || !isLoaded) {
      setLoading(false);
      return null;
    }
    if (!isSignedIn) {
      setState(DEFAULT_STATE);
      setErrors({});
      setLoading(false);
      setHasLoaded(true);
      return null;
    }

    setLoading(true);
    setErrors({});
    const entries = [
      ['connection', getBrokerLiveConnection()],
      ['positions', getBrokerLivePositions()],
      ['accountState', getBrokerLiveAccountState()],
      ['accountStateStatus', getBrokerLiveAccountStateStatus()],
      ['recentExecutions', getBrokerLiveRecentExecutions()],
      ['executionsStatus', getBrokerLiveExecutionsStatus()],
      ['syncStatus', getBrokerLiveSyncStatus()],
    ];

    const results = await Promise.allSettled(entries.map(([, promise]) => promise));
    if (!mountedRef.current) return null;

    const next = { ...DEFAULT_STATE };
    const nextErrors = {};
    entries.forEach(([key], index) => {
      const result = results[index];
      if (result.status === 'rejected') {
        nextErrors[key] = result.reason;
        return;
      }
      const payload = result.value;
      if (key === 'connection') {
        next.connection = normalizeConnection(payload);
      } else if (key === 'positions') {
        next.positions = normalizeSnapshot(payload, {
          label: 'Live position snapshots',
          itemKeys: ['positions'],
        });
      } else if (key === 'accountState') {
        next.accountState = normalizeSnapshot(payload, {
          label: 'Account state snapshot',
          itemKeys: ['account_states', 'account_state', 'accounts', 'balances', 'rows', 'items'],
        });
      } else if (key === 'accountStateStatus') {
        next.accountStateStatus = normalizeSnapshot(payload, {
          label: 'Account state status',
          itemKeys: ['accounts', 'items'],
        });
      } else if (key === 'recentExecutions') {
        next.recentExecutions = normalizeSnapshot(payload, {
          label: 'Near-live recent executions',
          itemKeys: ['executions', 'recent_executions', 'results', 'items'],
        });
      } else if (key === 'executionsStatus') {
        next.executionsStatus = normalizeSnapshot(payload, {
          label: 'Near-live execution status',
          itemKeys: ['executions', 'items'],
        });
      } else if (key === 'syncStatus') {
        next.syncStatus = normalizeSnapshot(payload, {
          label: 'Broker-live sync status',
          itemKeys: ['jobs', 'items'],
        });
      }
    });

    setState(next);
    setErrors(nextErrors);
    setLoading(false);
    setHasLoaded(true);
    return next;
  }, [enabled, isLoaded, isSignedIn]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshPositions = useCallback(async () => {
    if (!enabled || !isLoaded || !isSignedIn) return null;
    setPendingAction('positions');
    try {
      await refreshBrokerLivePositions();
      const positions = await getBrokerLivePositions();
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          positions: normalizeSnapshot(positions, { label: 'Live position snapshots', itemKeys: ['positions'] }),
        }));
      }
      return positions;
    } catch (err) {
      if (mountedRef.current) setErrors((prev) => ({ ...prev, positions: err }));
      return null;
    } finally {
      if (mountedRef.current) setPendingAction(null);
    }
  }, [enabled, isLoaded, isSignedIn]);

  const refreshExecutions = useCallback(async () => {
    if (!enabled || !isLoaded || !isSignedIn) return null;
    setPendingAction('executions');
    try {
      await refreshBrokerLiveExecutions();
      const [recentResult, statusResult, syncResult] = await Promise.allSettled([
        getBrokerLiveRecentExecutions(),
        getBrokerLiveExecutionsStatus(),
        getBrokerLiveSyncStatus(),
      ]);
      if (mountedRef.current) {
        setState((prev) => ({
          ...prev,
          recentExecutions:
            recentResult.status === 'fulfilled'
              ? normalizeSnapshot(recentResult.value, {
                  label: 'Near-live recent executions',
                  itemKeys: ['executions', 'recent_executions', 'results', 'items'],
                })
              : prev.recentExecutions,
          executionsStatus:
            statusResult.status === 'fulfilled'
              ? normalizeSnapshot(statusResult.value, {
                  label: 'Near-live execution status',
                  itemKeys: ['executions', 'items'],
                })
              : prev.executionsStatus,
          syncStatus:
            syncResult.status === 'fulfilled'
              ? normalizeSnapshot(syncResult.value, { label: 'Broker-live sync status', itemKeys: ['jobs', 'items'] })
              : prev.syncStatus,
        }));
        setErrors((prev) => {
          const next = { ...prev };
          if (recentResult.status === 'rejected') next.recentExecutions = recentResult.reason;
          else delete next.recentExecutions;
          if (statusResult.status === 'rejected') next.executionsStatus = statusResult.reason;
          else delete next.executionsStatus;
          if (syncResult.status === 'rejected') next.syncStatus = syncResult.reason;
          else delete next.syncStatus;
          return next;
        });
      }
      return {
        recentExecutions: recentResult.status === 'fulfilled' ? recentResult.value : null,
        executionsStatus: statusResult.status === 'fulfilled' ? statusResult.value : null,
        syncStatus: syncResult.status === 'fulfilled' ? syncResult.value : null,
      };
    } catch (err) {
      if (mountedRef.current) setErrors((prev) => ({ ...prev, executionsStatus: err }));
      return null;
    } finally {
      if (mountedRef.current) setPendingAction(null);
    }
  }, [enabled, isLoaded, isSignedIn]);

  return {
    ...state,
    loading,
    hasLoaded,
    errors,
    pendingAction,
    reload: load,
    refreshPositions,
    refreshExecutions,
  };
}
